"use client";

import { clsx } from "clsx";
import Image from "next/image";
import Link from "next/link";
import { RadarChart } from "./RadarChart";
import { BLUR_DATA } from "./blurData";
import { Button } from "@/components/ui/Button";
import { Eyebrow } from "@/components/ui/Eyebrow";
import { ScoreBar } from "@/components/ui/ScoreBar";
import { ArrowLeft, ArrowRight } from "@/components/ui/icons/Arrow";
import { DIMENSIONS, DIMENSION_LABELS } from "./quizSchemas";
import type { TripProfileDraft } from "./quizSchemas";
import type { PrioritiesDraft } from "./QuizStepPriorities";
import type { ConstraintsDraft } from "./QuizStepConstraints";
import { formatBudget, SHOW_PREFERRED_DESTINATION } from "./quizConstants";
import {
  humanizeGeoValue,
  type GeographyOptions,
} from "@/lib/api/recommendations/geography";

// Radar visual ordering — intentionally different from the step-display order in DIMENSIONS.
// Labels are derived from DIMENSION_LABELS so they can never drift from the canonical list.
const RADAR_ORDER = ["wildlife", "service", "luxury", "value", "eco", "authenticity"] as const;
const RADAR_AXIS_LABELS = RADAR_ORDER.map((k) => DIMENSION_LABELS[k]);

function SectionHeader({
  label,
  onEdit,
}: {
  label: string;
  onEdit?: () => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <Eyebrow tone="muted" tracking="0.18em">{label}</Eyebrow>
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          className="relative border-0 bg-transparent text-primary text-xs font-semibold cursor-pointer py-1 px-1.5 after:absolute after:-inset-2.5 after:content-['']"
        >
          Edit
        </button>
      )}
    </div>
  );
}

function DetailRow({
  label,
  value,
  last,
}: {
  label: string;
  value: string;
  last?: boolean;
}) {
  return (
    <div
      className={clsx(
        "flex items-center justify-between gap-4 py-3",
        !last && "border-b border-border",
      )}
    >
      <span className="text-text-muted text-sm font-medium">{label}</span>
      <span className="tabular-nums text-canopy-800 text-base font-semibold">
        {value}
      </span>
    </div>
  );
}

export function QuizStepReview({
  trip,
  priorities,
  constraints,
  onEdit,
  onBack,
  onSubmit,
  submitBusy,
  disabled,
  geoOptions,
}: {
  trip: TripProfileDraft;
  priorities: PrioritiesDraft;
  constraints: ConstraintsDraft;
  onEdit: (step: 1 | 2) => void;
  onBack?: () => void;
  onSubmit: () => void;
  submitBusy?: boolean;
  disabled?: boolean;
  geoOptions: GeographyOptions | null;
}) {
  const radarValues = RADAR_ORDER.map((k) => priorities[k]);

  const countryLabel = (() => {
    if (!constraints.preferredCountry) return "No preference";
    const found = geoOptions?.countries.find((c) => c.value === constraints.preferredCountry);
    return found?.label ?? humanizeGeoValue(constraints.preferredCountry);
  })();

  const regionLabel = (() => {
    if (!constraints.preferredCountry) return null; // row hidden when no country chosen
    if (!constraints.preferredRegion) return "Any region";
    const areas = geoOptions?.areas_by_country[constraints.preferredCountry] ?? [];
    const found = areas.find((a) => a.value === constraints.preferredRegion);
    return found?.label ?? humanizeGeoValue(constraints.preferredRegion);
  })();

  const childrenLabel =
    trip.childrenUnder12 === null
      ? // eslint-disable-next-line no-restricted-syntax -- em dash used as the standard empty-value glyph, not prose
        "—"
      : trip.childrenUnder12 === 0
        ? "None"
        : trip.childrenUnder12 === 1
          ? "1 child under 12"
          : `${trip.childrenUnder12} children under 12`;

  const accessibilityLabel = (() => {
    // eslint-disable-next-line no-restricted-syntax -- em dash used as the standard empty-value glyph, not prose
    if (trip.hasDisabilities === null) return "—";
    if (trip.hasDisabilities === false) return "None";
    const types = [
      trip.disabilityMobility && "Mobility",
      trip.disabilityVisual && "Visual",
      trip.disabilityHearing && "Hearing",
    ].filter(Boolean) as string[];
    return types.length > 0 ? types.join(", ") : "Not specified";
  })();

  const soloLabel =
    // eslint-disable-next-line no-restricted-syntax -- em dash used as the standard empty-value glyph, not prose
    trip.soloTraveller === true ? "Yes" : trip.soloTraveller === false ? "No" : "—";

  return (
    <div className="bg-surface min-h-full pt-6 px-4 pb-8 sm:pt-8 sm:px-6 sm:pb-12">
      <div className="max-w-[1180px] mx-auto">

        <div className="bg-surface-muted border border-border rounded-lg p-4 sm:p-9">
          <section className="relative isolate flex min-h-[212px] sm:min-h-[248px] flex-col justify-end overflow-hidden rounded-panel px-[22px] py-[26px] sm:px-9 sm:py-[34px]">
            {/* Decorative photo */}
            <div aria-hidden="true" className="absolute inset-0">
              <Image
                src="/quiz/review-banner.jpg"
                alt=""
                fill
                priority
                sizes="(max-width: 1180px) 100vw, 1132px"
                className="scale-[1.02] object-cover [object-position:center_42%]"
                placeholder="blur"
                blurDataURL={BLUR_DATA["/quiz/review-banner.jpg"]}
              />
            </div>
            {/* Legibility scrim */}
            <div
              aria-hidden="true"
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(90deg, rgba(2,42,24,.86) 0%, rgba(2,42,24,.62) 34%, rgba(2,42,24,.18) 70%, rgba(2,42,24,.05) 100%)," +
                  "linear-gradient(0deg, rgba(2,42,24,.5), rgba(2,42,24,.05) 55%)",
              }}
            />
            <Eyebrow tone="inherit" size="base" className="relative z-[2] text-canopy-100">
              Review
            </Eyebrow>
            <h1 className="relative z-[2] mt-3 text-3xl sm:text-display-sm font-bold tracking-tight leading-[1.05] text-balance text-white">
              Your preference summary
            </h1>
            <p className="relative z-[2] mt-3 max-w-quiz-prose text-base text-white/90">
              Take a moment to review your preferences and make sure everything feels right
            </p>
          </section>

          <div
            data-testid="review-grid"
            className="grid gap-4 mt-6 sm:gap-5 sm:mt-7"
            style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))" }}
          >
            {/* LEFT — Priority Vector */}
            <div className="bg-surface border border-border rounded-panel p-4 sm:p-[22px]">
              <SectionHeader label="SAFARI SNAPSHOT" onEdit={() => onEdit(1)} />
              {/* aria-hidden: the ScoreBars below already convey every dimension/value as text. */}
              <div aria-hidden="true" className="flex justify-center mt-2">
                <RadarChart values={radarValues} size={280} axisLabels={RADAR_AXIS_LABELS} />
              </div>
              <div className="mt-5 sm:mt-6 flex flex-col gap-3">
                {DIMENSIONS.map((a) => (
                  <ScoreBar
                    key={a.key}
                    label={a.label}
                    value={priorities[a.key]}
                    max={3}
                    display="ratio"
                    tone="canopy"
                  />
                ))}
              </div>
            </div>

            {/* RIGHT — Trip details */}
            <div className="flex flex-col gap-4">
              <div className="bg-surface border border-border rounded-panel p-4 sm:p-[22px]">
                <SectionHeader label="TRIP DETAILS" onEdit={() => onEdit(2)} />
                <div className="mt-3 flex flex-col">
                  {SHOW_PREFERRED_DESTINATION && (
                    <DetailRow label="Country" value={countryLabel} />
                  )}
                  {SHOW_PREFERRED_DESTINATION && regionLabel !== null && (
                    <DetailRow label="Region" value={regionLabel} />
                  )}
                  <DetailRow label="Children" value={childrenLabel} />
                  <DetailRow label="Travelling solo" value={soloLabel} />
                  <DetailRow label="Accessibility" value={accessibilityLabel} />
                  <DetailRow
                    label="Budget"
                    // Review is only reachable once canContinue passed, so budget is
                    // always set by the time this renders — "Not set" is unreachable.
                    // The flexibility qualifier is part of the value string, not a
                    // separate row or chip (design handoff).
                    value={
                      constraints.budget === null
                        ? "Not set"
                        : `${constraints.budgetFlexibility === "max" ? "up to" : "around"} ${formatBudget(constraints.budget, constraints.currency)} pp/night`
                    }
                    last
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 sm:mt-9 flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-3">
            <Button
              variant="secondary"
              size="md-base"
              type="button"
              onClick={onBack ?? undefined}
              disabled={!onBack || disabled}
              className="gap-2 w-full sm:w-auto"
            >
              <ArrowLeft /> Back
            </Button>
            <Button
              variant="cta"
              size="md-base"
              loading={submitBusy}
              onClick={onSubmit}
              disabled={disabled}
              className="gap-2 w-full sm:w-auto"
            >
              Find my lodges <ArrowRight />
            </Button>
          </div>
        </div>

        <div className="mt-4 sm:mt-6">
          <Link href="/" className="text-primary text-sm font-medium underline underline-offset-[3px]">
            Back home
          </Link>
        </div>
      </div>
    </div>
  );
}
